import { FC } from 'react'
import { useForm } from 'react-hook-form'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import {
	Button,
	Container,
	Form,
	FormField,
	Header,
	Input,
	SpaceBetween,
	Alert
} from '@cloudscape-design/components'
import LoadingSpinner from '../../../../components/LoadingSpinner'
import ApplicationDetails from '../../../../components/MongoDB/ApplicationDetails'
import ClusterConfiguration from '../../../../components/MongoDB/ClusterConfiguration'
import RequestError, {
	getMongoDBErrorMessage
} from '../../../../components/MongoDB/RequestError'
import {
	getMongoDBClusterFormValues,
	mongodbClusterResolver,
	prepareMongoDBCluster
} from '../../../../helper/mongodbClusterConfig'
import type { MongoDBClusterFormValues } from '../../../../helper/mongodbClusterConfig'
import type { MongoDBRequest } from '../../../../types/mongodb'
import { useOktaAuth } from '@okta/okta-react'
import useSingleMongoDB from '../../../../hooks/query/useSingleMongoDB'
import useUserPermissions from '../../../../hooks/query/useUserPermissions'
import useUpdateMongoDBDetails from '../../../../hooks/mutate/useUpdateMongoDBDetails'
import hasRequiredPermissions from '../../../../helper/hasRequiredPermissions'

interface MongoDBReopenFormProps {
	request: MongoDBRequest
}

const MongoDBReopenForm: FC<MongoDBReopenFormProps> = ({ request }) => {
	const navigate = useNavigate()
	const clusterForm = useForm<MongoDBClusterFormValues>({
		defaultValues: getMongoDBClusterFormValues(request.details.cluster),
		resolver: mongodbClusterResolver
	})
	const {
		mutate: updateMongoDBDetails,
		isLoading: isUpdating,
		isError,
		error
	} = useUpdateMongoDBDetails(request.mongoDBID)

	const handleSubmit = async () => {
		if (isUpdating) return
		if (!(await clusterForm.trigger())) return
		const result = prepareMongoDBCluster(clusterForm.getValues())
		if (result.status === 'invalid') return
		updateMongoDBDetails(
			{
				action: 'UPDATE_DETAILS',
				mongoDBID: request.mongoDBID,
				details: { cluster: result.cluster }
			},
			{
				onSuccess: () =>
					navigate(`/mongodb/${request.mongoDBID}`, {
						state: {
							notice: 'Corrections submitted. Refresh to check they were saved.'
						}
					})
			}
		)
	}

	return (
		<Form
			header={<Header variant="h1">Correct MongoDB Request</Header>}
			actions={
				<SpaceBetween direction="horizontal" size="xs">
					<Button
						variant="link"
						disabled={isUpdating}
						onClick={() => navigate(`/mongodb/${request.mongoDBID}`)}
					>
						Cancel
					</Button>
					<Button
						variant="primary"
						loading={isUpdating}
						disabled={isUpdating}
						onClick={() => void handleSubmit()}
					>
						Submit
					</Button>
				</SpaceBetween>
			}
		>
			<SpaceBetween direction="vertical" size="l">
				{isError && <Alert type="error">{getMongoDBErrorMessage(error)}</Alert>}
				<Container header={<Header variant="h2">MongoDB Request</Header>}>
					<FormField
						label="Application Name"
						description="The Application and environment cannot be changed for an existing request."
					>
						<Input
							value={`${request.applicationName} (${request.environment})`}
							readOnly
							disabled
						/>
					</FormField>
				</Container>
				<ClusterConfiguration form={clusterForm} disabled={isUpdating} />
				<ApplicationDetails details={request.details} />
			</SpaceBetween>
		</Form>
	)
}

const ReopenMongoDBRequest: FC = () => {
	const { id = '' } = useParams()
	const navigate = useNavigate()
	const { authState } = useOktaAuth()
	const email = String(authState?.idToken?.claims.email || '')
	const requestQuery = useSingleMongoDB(id)
	const accessQuery = useUserPermissions(email)
	const refresh = () => {
		void requestQuery.refetch()
		void accessQuery.refetch()
	}

	if (requestQuery.isLoading || accessQuery.isLoading) return <LoadingSpinner />
	const error = requestQuery.error || accessQuery.error
	if (error) return <RequestError error={error} retry={refresh} />
	const request = requestQuery.data
	if (!request) return <Alert>MongoDB request was not found.</Alert>
	if (request.status !== 'PENDING_APPROVAL' || !request.correctionPending) {
		return <Navigate to={`/mongodb/${id}`} replace />
	}
	const permissions = (accessQuery.data?.permissions || []).map((permission) =>
		permission.replace(/^mongodb:/i, 'mongodb:')
	)
	const canCorrect = hasRequiredPermissions(
		[`mongodb:${id}:read`, `mongodb:${id}:create`],
		permissions
	)
	if (!canCorrect) {
		return (
			<SpaceBetween direction="vertical" size="m">
				<Alert type="error">
					You do not have permission to save corrections for this request.
				</Alert>
				<Button onClick={() => navigate(`/mongodb/${id}`)}>
					Back to request
				</Button>
			</SpaceBetween>
		)
	}
	return (
		<MongoDBReopenForm
			key={`${request.mongoDBID}:${request.lastUpdatedTime}`}
			request={request}
		/>
	)
}

export default ReopenMongoDBRequest
